import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Loader2 } from "lucide-react";
import { WebsiteJsonLd, OrganizationJsonLd } from "@/components/seo/JsonLd";
import { TourProvider, TourOverlay } from "@/components/tour";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";

// Critical pages loaded immediately
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages for code splitting
const SignupCanceled = lazy(() => import("./pages/SignupCanceled"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SubscriptionRequired = lazy(() => import("./pages/SubscriptionRequired"));

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Library = lazy(() => import("./pages/Library"));
const Artists = lazy(() => import("./pages/Artists"));
const ArtistDetail = lazy(() => import("./pages/ArtistDetail"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Bookmarks = lazy(() => import("./pages/Bookmarks"));
const Popular = lazy(() => import("./pages/Popular"));
const Featured = lazy(() => import("./pages/Featured"));
const RecentlyPlayed = lazy(() => import("./pages/RecentlyPlayed"));

const Playlists = lazy(() => import("./pages/Playlists"));
const PlaylistDetail = lazy(() => import("./pages/PlaylistDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const VideoPlayer = lazy(() => import("./pages/VideoPlayer"));
const VideoPlayerPublic = lazy(() => import("./pages/VideoPlayerPublic"));
const About = lazy(() => import("./pages/About"));
const Plans = lazy(() => import("./pages/Plans"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const EducatorPartnershipThankYou = lazy(() => import("./pages/EducatorPartnershipThankYou"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

// Admin Pages - lazy loaded
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminVideoEdit = lazy(() => import("./pages/admin/AdminVideoEdit"));
const AdminSeries = lazy(() => import("./pages/admin/AdminSeries"));
const AdminSeriesEdit = lazy(() => import("./pages/admin/AdminSeriesEdit"));
const AdminArtists = lazy(() => import("./pages/admin/AdminArtists"));
const AdminArtistEdit = lazy(() => import("./pages/admin/AdminArtistEdit"));
const AdminTags = lazy(() => import("./pages/admin/AdminTags"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminContact = lazy(() => import("./pages/admin/AdminContact"));
const AdminEmailLogs = lazy(() => import("./pages/admin/AdminEmailLogs"));

// Series pages - lazy loaded
const SeriesPage = lazy(() => import("./pages/Series"));
const SeriesDetail = lazy(() => import("./pages/SeriesDetail"));
const SeriesDetailPublic = lazy(() => import("./pages/SeriesDetailPublic"));
const ArtistDetailPublic = lazy(() => import("./pages/ArtistDetailPublic"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Page transition wrapper for public pages
const publicPageVariants = {
  initial: { opacity: 0, y: 60, scale: 0.95 },
  animate: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }
  },
  exit: { 
    opacity: 0, 
    y: -40, 
    scale: 0.98,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }
  },
};

function PublicPageWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <motion.div
      key={location.pathname}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={publicPageVariants}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicPageWrapper><Index /></PublicPageWrapper>} />
          <Route path="/login" element={<PublicPageWrapper><Login /></PublicPageWrapper>} />
          <Route path="/signup" element={<PublicPageWrapper><Signup /></PublicPageWrapper>} />
          <Route path="/signup-canceled" element={<PublicPageWrapper><SignupCanceled /></PublicPageWrapper>} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<PublicPageWrapper><ResetPassword /></PublicPageWrapper>} />
          <Route path="/subscription-required" element={<SubscriptionRequired />} />
          
          <Route path="/about" element={<PublicPageWrapper><About /></PublicPageWrapper>} />
          <Route path="/plans" element={<PublicPageWrapper><Plans /></PublicPageWrapper>} />
          <Route path="/faq" element={<PublicPageWrapper><FAQ /></PublicPageWrapper>} />
          <Route path="/contact" element={<PublicPageWrapper><Contact /></PublicPageWrapper>} />
          <Route path="/educator-partnership-thank-you" element={<PublicPageWrapper><EducatorPartnershipThankYou /></PublicPageWrapper>} />
          <Route path="/terms" element={<PublicPageWrapper><Terms /></PublicPageWrapper>} />
          <Route path="/privacy" element={<PublicPageWrapper><Privacy /></PublicPageWrapper>} />
          
          {/* Public content pages - crawlable for SEO, content gated */}
          <Route path="/video/:id" element={<PublicPageWrapper><VideoPlayerPublic /></PublicPageWrapper>} />
          <Route path="/series/:id" element={<PublicPageWrapper><SeriesDetailPublic /></PublicPageWrapper>} />
          <Route path="/artist/:id" element={<PublicPageWrapper><ArtistDetailPublic /></PublicPageWrapper>} />

          {/* Protected routes - require active subscription for content pages */}
          <Route path="/dashboard" element={<ProtectedRoute requireActiveSubscription><Dashboard /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute requireActiveSubscription><Library /></ProtectedRoute>} />
          <Route path="/artists" element={<ProtectedRoute requireActiveSubscription><Artists /></ProtectedRoute>} />
          <Route path="/playlists" element={<ProtectedRoute requireActiveSubscription><Playlists /></ProtectedRoute>} />
          <Route path="/playlist/:id" element={<ProtectedRoute requireActiveSubscription><PlaylistDetail /></ProtectedRoute>} />
          <Route path="/watchlist" element={<ProtectedRoute requireActiveSubscription><Watchlist /></ProtectedRoute>} />
          <Route path="/bookmarks" element={<ProtectedRoute requireActiveSubscription><Bookmarks /></ProtectedRoute>} />
          <Route path="/popular" element={<ProtectedRoute requireActiveSubscription><Popular /></ProtectedRoute>} />
          <Route path="/featured" element={<ProtectedRoute requireActiveSubscription><Featured /></ProtectedRoute>} />
          <Route path="/recently-played" element={<ProtectedRoute requireActiveSubscription><RecentlyPlayed /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/series" element={<ProtectedRoute requireActiveSubscription><SeriesPage /></ProtectedRoute>} />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/videos" replace />} />
          <Route path="/admin/videos" element={<ProtectedAdminRoute><AdminVideos /></ProtectedAdminRoute>} />
          <Route path="/admin/videos/new" element={<ProtectedAdminRoute><AdminVideoEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/videos/edit/:id" element={<ProtectedAdminRoute><AdminVideoEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/series" element={<ProtectedAdminRoute><AdminSeries /></ProtectedAdminRoute>} />
          <Route path="/admin/series/new" element={<ProtectedAdminRoute><AdminSeriesEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/series/edit/:id" element={<ProtectedAdminRoute><AdminSeriesEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/artists" element={<ProtectedAdminRoute><AdminArtists /></ProtectedAdminRoute>} />
          <Route path="/admin/artists/new" element={<ProtectedAdminRoute><AdminArtistEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/artists/edit/:id" element={<ProtectedAdminRoute><AdminArtistEdit /></ProtectedAdminRoute>} />
          <Route path="/admin/tags" element={<ProtectedAdminRoute><AdminTags /></ProtectedAdminRoute>} />
          <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
          <Route path="/admin/analytics" element={<ProtectedAdminRoute><AdminAnalytics /></ProtectedAdminRoute>} />
          <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
          <Route path="/admin/contact" element={<ProtectedAdminRoute><AdminContact /></ProtectedAdminRoute>} />
          <Route path="/admin/email-logs" element={<ProtectedAdminRoute><AdminEmailLogs /></ProtectedAdminRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
  );
}
const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TourProvider>
          <TooltipProvider>
            {/* Global SEO Schema */}
            <WebsiteJsonLd />
            <OrganizationJsonLd />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <AnalyticsProvider />
              <AnimatedRoutes />
              <TourOverlay />
            </BrowserRouter>
          </TooltipProvider>
        </TourProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
